import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ConfigCacheService } from './config-cache.service';

@Injectable({
  providedIn: 'root',
})
export class ListingPropertiesService {
  constructor(
    private api: ApiService,
    private configCache: ConfigCacheService
  ) {}

  getListingProperties(pageNumber = 1, pageSize = 15, payload: any): Observable<any> {
    const { isRental = false, isGridView = false, columns } = payload;
    return this.api.get<any>(
      `/listing?office_id=${this.api.getOfficeId()}&associate_property=false&page=${pageNumber}&per_page=${pageSize}&gridview=${isGridView}&only_rental=${isRental}${columns ? `&columns=${columns}` : ''}`
    );
  }

  getShortListing(
    isAssociated: boolean = false,
    isSattled: boolean = true,
    isTiny: boolean = true,
    isGridView: boolean = false,
    isSold: boolean = false
  ): Observable<any> {
    return this.api.get<any>(
      `/listing?office_id=${this.api.getOfficeId()}&page=1&per_page=99999&associate_property=${isAssociated}&sattled=${isSattled}&is_tiny=${isTiny}&gridview=${isGridView}&isSold=${isSold}`
    );
  }

  /**
   * Everything the listing drawer's Dashboard tab shows, in one read. `sections` narrows the
   * response to the panels that actually changed — the Leads period dropdown asks for `leads`
   * alone rather than re-reading the whole dashboard.
   */
  getListingDashboard(listingId: string, options: { leadPeriod?: string; sections?: string[]; isProjectManager?: boolean } = {}): Observable<any> {
    const params = new URLSearchParams({
      office_id: this.api.getOfficeId(),
      user_id: this.api.getUserId(),
    });

    if (options.leadPeriod) {
      params.set('lead_period', options.leadPeriod);
    }
    if (options.sections?.length) {
      params.set('sections', options.sections.join(','));
    }
    if (options.isProjectManager) {
      params.set('is_project_manager', 'true');
    }

    return this.api.get<any>(`/listing-dashboard/${listingId}?${params.toString()}`);
  }

  contractFilled(id: string): Observable<any> {
    return this.api.get<any>(`/listing/contract-pre-fill-data/${id}`);
  }

  getListingPropertiesByFilters(pageNumber = 1, pageSize = 15, payload: any): Observable<any> {
    return this.api.post<any>(`/listing/filter?page=${pageNumber}&per_page=${pageSize}&associate_property=false`, payload);
  }

  getComparedListingResults(listing_ids: string[]) {
    return this.api.post('/listing-compare', { office_id: this.api.getOfficeId(), listing_ids: listing_ids });
  }

  getListingPropertyById(propertyId: string) {
    return this.api.get(`/listing/${propertyId}`);
  }

  deletePropertyById(propertyId: string) {
    return this.api.delete(`/listing/${propertyId}`);
  }

  getPropertyDetails(propertyId: string, ofi_id: any) {
    return this.api.get(`/ofi/listing/leads/${propertyId}?ofi_id=${ofi_id}&office_id=${this.api.getOfficeId()}`);
  }

  getContactDetails(contactId: string, lead_id: any) {
    return this.api.get(`/ofi/listing/contact-related-listings/${contactId}?office_id=${this.api.getOfficeId()}&lead_id=${lead_id}`);
  }

  updateOfiCheckInStatus(payload: any) {
    return this.api.post('/ofi/listing/checkin', payload);
  }

  deleteListingNotes(_id: any) {
    return this.api.delete(`/ofi/listing/lead-note-remove/${_id}?office_id=${this.api.getOfficeId()}`);
  }

  up_dateListingNotes(_id: any, payload: any) {
    return this.api.put(`/ofi/listing/leads-note-update/${_id}?office_id=${this.api.getOfficeId()}`, payload);
  }

  registerListingLead(payload: any) {
    return this.api.post('/ofi/listing/register-lead', payload);
  }

  listingPreviewEmailToSeller(id: any, payload: any) {
    return this.api.post('/listing-approval/email-to-seller/' + id, payload);
  }

  updateRegisterListingLead(id: any, payload: any) {
    return this.api.put('/ofi/listing/register-lead/' + id, payload);
  }

  getListingShortDetails() {
    return this.api.get(`/listing/short-detail?office_id=${this.api.getOfficeId()}`);
  }

  getVisibility() {
    return this.api.get('/settings/visibility/' + this.api.getOfficeId() + '/' + 'vendor_comments');
  }

  addListingProperty(payload: any) {
    return this.api.post(`/listing`, payload);
  }
  up_dateListingProperty(propertyId: string, payload: any) {
    return this.api.put(`/listing/${propertyId}`, payload);
  }

  getListingPropertyDetailById(propertyId: string) {
    return this.api.get(`/listing/full-detail/${propertyId}?office_id=${this.api.getOfficeId()}`);
  }

  assignedOtherAgent(payload: any) {
    // Deactivating an agent changes who shows up in the byoffice dropdown and office-staff-list grid.
    return this.api.put(`/listing/deactive_agent`, payload).pipe(
      tap(() => {
        this.configCache.evict('/users/byoffice');
        this.configCache.evict('/users/office-staff-list');
      })
    );
  }

  getListingListAgainstPropertyId(propertyId: string) {
    return this.api.get(`/property/all-listings/${propertyId}`);
  }

  getListingAllSuburb() {
    return this.api.get(`/listing/all-suburbs?office_id=${this.api.getOfficeId()}`);
  }
}
